import {
	isActivationKey,
	measureMarkerShift,
	textLeftOf,
	writeMarkerShift,
} from "@ext/git/core/Diff/logic/collapseDeleted";
import type { MovedMarker } from "@ext/git/core/Diff/logic/movedMarkers";
import { visiblePos } from "@ext/git/core/Diff/logic/onlyChanges";
import scrollToDiff from "@ext/git/core/Diff/logic/scrollToDiff";
import t from "@ext/localization/locale/translate";
import { Plugin, PluginKey } from "prosemirror-state";
import { Decoration, DecorationSet, type EditorView } from "prosemirror-view";

export const movedMarkersKey = new PluginKey<DecorationSet>("moved-markers");

const HIGHLIGHT_CLASS = "diff-moved-target";
const HIGHLIGHT_MS = 1200;

const hintOf = (marker: MovedMarker): string => {
	const direction = t(`diff.moved.${marker.side}-direction-${marker.direction}`);
	const title = t(`diff.moved.${marker.side}-title`).replace("{direction}", direction);
	const body =
		marker.side === "from"
			? t("diff.moved.from-body").replace("{preview}", marker.preview)
			: t("diff.moved.to-body");
	const hint = t(`diff.moved.${marker.side}-hint`);

	return `${title}\n${body}\n${hint}`;
};

/** Geometry is tuned by hand: the `top` offsets in styles depend on this viewBox. */
const ARROW = {
	to: '<svg viewBox="0 -14 23 38" width="23" height="38" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M13.6 3V7.1a6.54 6.54 0 0 1-3.27 5.66"/><path d="M4.7 16.01 12.33 16.22 8.33 9.3Z" fill="currentColor" stroke="none"/></svg>',
};

const buildMarker = (marker: MovedMarker) => {
	const element = document.createElement("span");
	element.className = `diff-moved-marker diff-moved-${marker.side} diff-moved-${marker.direction}`;
	element.innerHTML = marker.side === "from" ? '<span class="diff-moved-triangle"></span>' : ARROW.to;
	element.dataset.pairId = marker.pairId;
	element.dataset.side = marker.side;
	element.setAttribute("role", "button");
	element.setAttribute("tabindex", "0");
	element.setAttribute("title", hintOf(marker));
	element.contentEditable = "false";
	return element;
};

const decorationsFor = (markers: MovedMarker[], docSize: number) =>
	markers
		.filter((marker) => marker.pos >= 0 && marker.pos <= docSize)
		.map((marker) =>
			Decoration.widget(marker.pos, () => buildMarker(marker), {
				side: -1,
				key: `${marker.pairId}-${marker.side}`,
			}),
		);

const ARROW_CENTER = { down: 23.6, up: 14.4 };

type ArrowTop = { marker: HTMLElement; top: string };

const measureArrows = (view: EditorView): ArrowTop[] =>
	[...view.dom.querySelectorAll<HTMLElement>(".diff-moved-to")].flatMap((marker) => {
		const up = marker.classList.contains("diff-moved-up");
		const block = up ? marker.previousElementSibling : marker.nextElementSibling;
		if (!block) return [];
		const box = block.getBoundingClientRect();
		const offset = box.top + box.height / 2 - marker.getBoundingClientRect().top;
		return [{ marker, top: `${Math.round(offset - (up ? ARROW_CENTER.up : ARROW_CENTER.down))}px` }];
	});

const writeArrow = ({ marker, top }: ArrowTop) => {
	if (marker.style.getPropertyValue("--diff-moved-top") !== top) marker.style.setProperty("--diff-moved-top", top);
};

const align = (view: EditorView) => {
	const textLeft = textLeftOf(view);
	const shifts = [...view.dom.querySelectorAll<HTMLElement>(".diff-moved-marker")].map((marker) =>
		measureMarkerShift(textLeft, { block: marker, marker }),
	);
	const arrows = measureArrows(view);
	shifts.forEach(writeMarkerShift);
	arrows.forEach(writeArrow);
};

const highlight = (view: EditorView, pairId: string, side: "from" | "to") => {
	const element = view.dom.querySelector<HTMLElement>(`[data-pair-id="${pairId}"][data-side="${side}"]`);
	if (!element) return;

	element.classList.add(HIGHLIGHT_CLASS);
	setTimeout(() => element.classList.remove(HIGHLIGHT_CLASS), HIGHLIGHT_MS);
};

const jumpToPair = (view: EditorView, markers: MovedMarker[], pairId: string, from: "from" | "to") => {
	const own = markers.find((marker) => marker.pairId === pairId && marker.side !== from);
	if (!own) return;
	scrollToDiff(view, visiblePos(view.state, own.pos));
	highlight(view, pairId, own.side);
};

const movedMarkersPlugin = (getMarkers: () => MovedMarker[]) =>
	new Plugin<DecorationSet>({
		key: movedMarkersKey,
		state: {
			init: (_, state) => DecorationSet.create(state.doc, decorationsFor(getMarkers(), state.doc.content.size)),
			apply: (tr, set) =>
				tr.getMeta(movedMarkersKey)
					? DecorationSet.create(tr.doc, decorationsFor(getMarkers(), tr.doc.content.size))
					: set.map(tr.mapping, tr.doc),
		},
		view: (view) => {
			// Capture phase: ProseMirror's own keydown handler on the same element would split a block on Enter.
			const onKeyDown = (event: KeyboardEvent) => {
				if (!isActivationKey(event) || !(event.target instanceof Element)) return;
				const marker = event.target.closest<HTMLElement>(".diff-moved-marker");
				const { pairId, side } = marker?.dataset ?? {};
				if (!pairId || !side) return;

				event.preventDefault();
				event.stopPropagation();
				jumpToPair(view, getMarkers(), pairId, side as "from" | "to");
			};

			view.dom.addEventListener("keydown", onKeyDown, true);
			align(view);
			const observer = typeof ResizeObserver === "undefined" ? null : new ResizeObserver(() => align(view));
			observer?.observe(view.dom);
			return {
				update: (updated) => align(updated),
				destroy: () => {
					observer?.disconnect();
					view.dom.removeEventListener("keydown", onKeyDown, true);
				},
			};
		},
		props: {
			decorations(state) {
				return this.getState(state);
			},
			handleClick(view, _pos, event) {
				const marker = (event.target as HTMLElement)?.closest<HTMLElement>(".diff-moved-marker");
				if (!marker) return false;

				const { pairId, side } = marker.dataset;
				if (!pairId || !side) return false;

				jumpToPair(view, getMarkers(), pairId, side as "from" | "to");
				return true;
			},
		},
	});

export default movedMarkersPlugin;
